<template>
  <div class="fts-workflow-bar">
    <template v-if="activity.id !== 'create'">
      <fts-activity-design-dropdown
        :activity="activity"
        :activities="activities"
        :from-state-id="currentState && currentState.id"
        :to-state-id="selectedState && selectedState.id"
        @activity:update="(val) => updateActivity(val)"
        @activity:delete="deleteActivity(activity.id)"
        class="wb-activity-container"
        v-if="designMode && !activity.standard"
      />
      <div class="wb-activity-container" v-else>
        <fts-label-group
          :item="activity"
          class="wb-activity-wrapper"
          v-if="activity.standard"
        />
        <fts-label-group
          :item="activity"
          emoji
          :emoji-size="16"
          :label-color="activity.color"
          class="wb-activity-wrapper"
          v-else
        />
      </div>
    </template>

    <div class="wb-state-container" v-if="isWorkflowVisible">
      <div>
        <div class="wb-label" data-name="standard-create" v-if="activity.id === 'create'">
          create
        </div>

        <fts-state
          :state="currentState"
          :states="states"
          data-name="from-state"
          v-else-if="isCurrentStateVisible"
        />

        <div class="wb-label fts-font-style-italic fts-text-default" v-else-if="isStateDesignMode">
          (no state)
        </div>

        <div class="wb-divider" v-if="isStateDesignMode || isNextStatesVisible">
          <font-awesome-icon :icon="['fas', 'right-long']" />
        </div>
        
        <fts-states-design-dropdown
          :states="states"
          :selected-state.sync="selectedState"
          :blank-option-label="activity.id === 'create' ? 'none' : 'same'"
          :initial-set-suggest-state="isInitialSetSuggestState"
          design-mode
          @option:select="(val) => setStateAndFlow(val, 'select')"
          @option:create="(val) => setStateAndFlow(val, 'create')"
          @option:clear="clearState"
          @state:update="(val) => updateState(val)"
          @state:delete="(val) => deleteState(val)"
          @state-assignee:update="(val) => updateStateAssignee(val)"
          v-if="isStateDesignMode"
        />

        <template v-else-if="isNextStatesVisible">
          <template v-for="(x, index) in nextStates">
            <fts-state
              :key="x.id"
              :state="x"
              :states="states"
              data-name="to-state"
            />
            <div
              :key="`or-${x.id}`"
              class="wb-divider"
              v-if="index != nextStates.length - 1" 
            >
              or
            </div>
          </template>
        </template>

        <fts-assign-to
          :module-id="moduleId"
          :form-data.sync="mutableFormData"
          :max-length="3"
          :view-mode="!assignRight"
          update-mode
          submit-on-close
          avatar-mode
          :avatar-size="26"
          :toggle-create="!(formData.assignees && formData.assignees.length)"
          :comment-section="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import { clientStore } from '../store/clientStore';
import canvasDesign from '../commons/canvasDesign';

import FTSActivityDesignDropdown from '../design/FTSActivityDesignDropdown';
import FTSAssignTo from './FTSAssignTo';
import FTSLabelGroup from './FTSLabelGroup';
import FTSState from './FTSState';
import FTSStatesDesignDropdown from '../design/FTSStatesDesignDropdown';

export default {
  components: {
    'fts-activity-design-dropdown': FTSActivityDesignDropdown,
    'fts-assign-to': FTSAssignTo,
    'fts-label-group': FTSLabelGroup,
    'fts-state': FTSState,
    'fts-states-design-dropdown': FTSStatesDesignDropdown,
  },
  props: {
    moduleId: [String, Number],
    states: Array,
    activities: Array,
    activity: Object,
    currentState: Object,
    nextStates: Array,
    formData: Object,
    assignRight: Boolean,
    commentRight: Boolean,
    designMode: Boolean
  },
  methods: {
    async updateActivity(item) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        this.$emit('update:activity', item);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);

        const activity = canvasDesign.getActivity(canvas, item.id);
        if (activity) {
          canvasDesign.setActivity(canvas, {
            ...activity,
            name: item.name,
            emoji: item.emoji
          });

          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'activity not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to update activity'
        }); 
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async deleteActivity(id) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const activity = canvasDesign.getActivity(canvas, id);
        if (activity) {
          canvasDesign.deleteActivity(canvas, activity);
          if (canvas.flows && canvas.flows.length) {
            let flows = canvas.flows.filter((x) => x.activityId === activity.id);
            if (flows.length) {
              flows.forEach((x) => {
                canvasDesign.deleteFlow(canvas, x);
              });
            }
          }
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
          this.$emit("activity:delete", activity.id);
        } else {
          throw 'activity not found'
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to delete activity'
        }); 
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    setFlows(canvas, fromStateId, activityId, toStateId, previousToStateId) {
      const flows = Array.isArray(canvas && canvas.flows) ?
        canvas.flows.filter((x) => 
          x.fromStateId == fromStateId &&
          x.activityId == activityId &&
          x.toStateId == previousToStateId &&
          !x.conditional
        ) : [];

      if (flows.length) {
        flows.forEach((x) => {
          canvasDesign.setFlow(canvas, {...x, toStateId: toStateId});
        });
      } else {
        const flow = canvasDesign.initFlow({ 
          fromStateId: fromStateId, 
          activityId: activityId,
          toStateId: toStateId
        });
        canvasDesign.setFlow(canvas, flow);
      }
    },
    async updateState(item) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, item.id);
        if (state) {
          canvasDesign.setState(canvas, {
            ...state,
            name: item.name,
            color: item.color
          });
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to update state'
        }); 
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async deleteState(id) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, id);
        if (state) {
          canvasDesign.deleteState(canvas, state);
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        } else {
          throw 'state not found';
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to delete state'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async setStateAndFlow(item, action) {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        const isCreateActivity = Boolean(this.activity && this.activity.standard && this.activity.id === 'create');
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let save = false;
        let state = {...item};

        if ((isCreateActivity && action === 'select') && !state.initial) {
          state.initial = true;
          canvasDesign.setState(canvas, state);
          save = true;
        } else if (action === 'create') {
          if (isCreateActivity) {
            state.initial = true;
          }
          state = canvasDesign.initState(state);
          canvasDesign.setState(canvas, state);
          save = true;
        }

        if (this.activity && this.activity.id && !this.activity.standard) {
          this.setFlows(
            canvas,
            this.currentState && this.currentState.id,
            this.activity.id,
            state.id,
            this.nextStates && this.nextStates[0] && this.nextStates[0].id
          );
          save = true;
        }

        if (save) {
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: res.data.updatedDate
          });
        }

        this.selectedState = state;
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: action === 'create' ? 'Failed to create state' : 'Failed to update state'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async clearState() {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let save = false;

        if (this.activity && this.activity.id && !this.activity.standard) {
          this.setFlows(
            canvas,
            this.currentState && this.currentState.id,
            this.activity.id,
            null,
            this.selectedState.id,
          );
          save = true;
        } else if (this.activity && this.activity.id === 'create') {
          if (canvas.states) {
            let initialStates = canvas.states.filter((x) => x.initial);
            if (initialStates.length) {
              initialStates.forEach((x) => { 
                x.initial = null;
              });
              save = true;
            }
          }
        }

        if (save) {
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const res = await canvasDesignStore.dispatch("saveVector");
          clientStore.dispatch("setWorkspaceModuleStates", {
            moduleId: res.data.id,
            states: canvas.states
          });
        }

        this.selectedState = null;
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed clear state'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    async updateStateAssignee(item) {
      if (item && item.stateId) {
        if (this.selectedState && this.selectedState.id === item.stateId) {
          this.mutableFormData = Object.assign({}, this.mutableFormData, { assignees: item.assignees });
        }
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const state = canvasDesign.getState(canvas, item.stateId);
        if (state) {
          try {
            clientStore.dispatch("setDesignModeLoading", true);
            canvasDesign.setState(canvas, {
              ...state,
              assignees: item.assignees
            });
            canvasDesign.compactDesignBeforeSave(canvas);
            canvasDesignStore.dispatch("updateVectorCanvas", canvas);
            const res = await canvasDesignStore.dispatch("saveVector");
            clientStore.dispatch("setDesignModeState", {
              success: true,
              updatedOn: res.data.updatedDate
            });
          } catch(err) {
            console.error(err);
            clientStore.dispatch("setDesignModeState", { error: true });
            this.$toasted.global.error({
              response: err.response,
              message: 'Failed to update state assignee'
            });
          } finally {
            clientStore.dispatch("setDesignModeLoading", false);
          }
        }
      }
    }
  },
  computed: {
    isWorkflowVisible() {
      return this.activity && 
        (
          !this.activity.standard || 
          (this.activity.standard && ['create', 'edit', 'changeStatus'].indexOf(this.activity.id) > -1)
        );
    },
    isStateDesignMode() {
      return this.designMode && (
        !this.nextStates ||
        (this.nextStates && this.nextStates.length <= 1)
      );
    },
    isCurrentStateVisible() {
      return Boolean(this.currentState && this.currentState.id);
    },
    isNextStatesVisible() {
      return Boolean(this.nextStates && this.nextStates.length);
    },
    isInitialSetSuggestState() {
      const isCreateWithoutForm = () => {
        let createForm = canvasDesignStore.getters.canvasStandardActivities.find((x) => x.id == "createEditView");
        if (createForm && createForm.design) {
          return false;
        } else {
          createForm = canvasDesignStore.getters.canvasStandardActivities.find((x) => x.id == "create");
          if (createForm && createForm.design) {
            return false;
          }
        }
        return true;
      }

      if (this.activity.id === 'create') {
        if (this.states && this.states.length) {
          return false;
        } else if (!this.isCurrentStateVisible && isCreateWithoutForm()) {
          return true;
        }
      }
      return false;
    },
    mutableFormData: {
      get() {
        return this.formData;
      },
      set(val) {
        this.$emit("update:formData", val);
      }
    },
    selectedState: {
      get() {
        if (this.activity && this.activity.standard) {
          const selectedState = this.mutableFormData && this.mutableFormData.selectedState;
          return this.activity.id === 'create' && !selectedState ?
            this.nextStates && this.nextStates[0] :
            selectedState;
        } else {
          return this.nextStates && this.nextStates[0];
        }
      },
      set(val) {
        if (this.activity && this.activity.standard) {
          this.mutableFormData = {...this.mutableFormData, selectedState: val};
        } else if (val) {
          this.$emit("update:nextStates", [val]);
        } else {
          this.$emit("update:nextStates", []);
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "src/assets/scss/vars.scss";

.fts-workflow-bar {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .wb-activity-container {
    display: flex;
    justify-content: flex-start;
    font-size: 1rem;
    .wb-activity-wrapper {
      border-bottom: 1px solid transparent;
    }
    & + .wb-state-container {
      margin-top: 0.375rem;
    }
  }
  .wb-state-container {
    > div {
      @include flex-gap($gap: 0.5rem);
      min-width: 0;
      align-items: center;
    }
  }
  .wb-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-text);
  }
  .wb-divider {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
}
</style>